import { socketURL } from './mount';
import { decodeFrame, encodeCommand, type Command, type ServerFrame } from './protocol';

/**
 * The server's *you were replaced* close code.
 *
 * 4000–4999 is the range WebSocket reserves for application use. Mirrored by hand from
 * `internal/net/client.go`'s `CloseDisplaced` — the two are walked against each other by a
 * spec, because a client that did not recognise this would reconnect, and reconnecting is
 * the entire loop the code exists to stop.
 */
export const CLOSE_DISPLACED = 4001;

/** How long to wait before the first reconnect attempt, in milliseconds. */
const RECONNECT_BASE_MS = 500;

/** Ceiling for the exponential backoff. */
const RECONNECT_MAX_MS = 8000;

export interface SocketOptions {
  /**
   * Which character to play. Turned into the `?character=` the server reads.
   *
   * Ignored when `url` is given outright, which is what every test does.
   */
  characterID?: string;
  /** Absolute socket URL. Defaults to /grind/ws on the current origin. */
  url?: string;
  /** Called for every decoded server frame. */
  onFrame: (frame: ServerFrame) => void;
  /** Called when the connection opens or closes, for the HUD's status pip. */
  onStatus?: (connected: boolean) => void;
  /**
   * Called when the server replaced this connection with a newer one for the same
   * character. **Terminal**: no reconnect follows, so the caller must say something — a
   * silent dead socket is indistinguishable from a frozen game.
   */
  onDisplaced?: () => void;
  /** Injectable for tests. Defaults to the global WebSocket. */
  socketFactory?: (url: string) => WebSocket;
  /** Injectable for tests. Defaults to setTimeout. */
  schedule?: (fn: () => void, ms: number) => void;
}

/**
 * Derives the socket URL from the page's own origin **and its mount point**, so the client
 * works unchanged behind the Caddy edge on :8088, against the Vite dev server on :5173, and
 * from the Gin static mount on `grind.townzone.net`.
 */
export function defaultSocketURL(
  location: { protocol: string; host: string },
  /**
   * Which character this socket is for.
   *
   * Absent on a server with no accounts, which connects anonymously exactly as the game did
   * before they existed. **The server re-checks it** — it looks the id up scoped to the
   * signed-in account, so naming somebody else's is refused and is indistinguishable from
   * naming one that does not exist.
   */
  characterID?: string,
): string {
  // The path comes from where the HUD is mounted, not from a second copy of the prefix —
  // `/grind/ws` behind the dev Caddy, `/ws` on `grind.townzone.net`. See `net/mount.ts`.
  return socketURL(characterID, location);
}

/**
 * The game connection: one WebSocket, reconnected with exponential backoff, and
 * a send queue that survives a drop.
 *
 * This is the only place the client talks to the server. It owns no game state —
 * it hands decoded frames to a callback and lets the store decide what they mean.
 */
export class GameSocket {
  private readonly url: string;
  private readonly onFrame: (frame: ServerFrame) => void;
  private readonly onStatus: (connected: boolean) => void;
  private readonly onDisplaced?: () => void;
  private readonly socketFactory: (url: string) => WebSocket;
  private readonly schedule: (fn: () => void, ms: number) => void;

  private ws: WebSocket | null = null;
  private retries = 0;
  private closed = false;

  /** Commands issued before the socket opened, replayed on connect. */
  private pending: string[] = [];

  constructor(opts: SocketOptions) {
    this.url = opts.url ?? defaultSocketURL(window.location, opts.characterID);
    this.onFrame = opts.onFrame;
    this.onStatus = opts.onStatus ?? (() => {});
    this.onDisplaced = opts.onDisplaced;
    this.socketFactory = opts.socketFactory ?? ((url) => new WebSocket(url));
    this.schedule = opts.schedule ?? ((fn, ms) => void setTimeout(fn, ms));
  }

  connect(): void {
    // Clear the explicit-close flag. Calling connect() *is* the intent to be
    // connected, and returning to the title screen and entering again is
    // precisely close-then-connect — without this it would silently do nothing.
    this.closed = false;

    const ws = this.socketFactory(this.url);
    this.ws = ws;

    ws.onopen = () => {
      this.retries = 0;
      this.onStatus(true);
      for (const frame of this.pending) ws.send(frame);
      this.pending = [];
    };

    ws.onmessage = (ev: MessageEvent) => {
      if (typeof ev.data !== 'string') return;
      const frame = decodeFrame(ev.data);
      // A frame we cannot read is not worth dropping the session over.
      if (frame) this.onFrame(frame);
    };

    ws.onclose = (ev: CloseEvent) => {
      this.ws = null;
      this.onStatus(false);

      // **A displaced connection must not reconnect.**
      //
      // Reconnecting on any close is right for a dropped network and catastrophic for
      // this one: the server replaces an older socket for the same character with a newer
      // one, so A reconnects, that displaces B, B reconnects, that displaces A — two tabs
      // on one character flapping at the backoff floor. Reported as *RECONNECTING every
      // second*.
      //
      // The close **code** is what breaks the loop, and it has to be respected here
      // because the server cannot stop a client that insists on dialling back.
      if (ev.code === CLOSE_DISPLACED) {
        this.closed = true;
        this.onDisplaced?.();
        return;
      }
      this.scheduleReconnect();
    };

    // An error is always followed by a close, which is where reconnection is
    // handled; this exists only so the event does not reach window.onerror.
    ws.onerror = () => {};
  }

  /**
   * Sends a command, or queues it if the socket is not open yet. Queued commands
   * are input, not state — they are replayed once, on the next successful open.
   */
  send(cmd: Command): void {
    const frame = encodeCommand(cmd);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(frame);
      return;
    }
    this.pending.push(frame);
  }

  /** Closes the connection for good; no reconnect follows. */
  close(): void {
    this.closed = true;
    this.ws?.close();
    this.ws = null;
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.retries, RECONNECT_MAX_MS);
    this.retries += 1;
    this.schedule(() => this.connect(), delay);
  }
}
